import type {
    PartnerCockpitContactDetail,
    PartnerCockpitOfferRequestDetail,
} from '@houzy/schemas/partner-cockpit/offer-requests';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { selectImpersonation, useCurrentUser } from '../../../lib/auth';
import { useTrackEvent } from '../../../lib/trackingEvents';
import { contactShortcutOf } from '../offerRequestTracking';
import { revealOfferRequestContactDetail } from './offerRequestsApi';
import { offerRequestDetailQueryKey } from './useOfferRequestDetail';

export function useRevealContactDetail(offerRequestLeadUuid: string): (detail: PartnerCockpitContactDetail) => void {
    const queryClient = useQueryClient();
    const track = useTrackEvent();
    const isImpersonating = selectImpersonation(useCurrentUser()) !== undefined;
    const queryKey = offerRequestDetailQueryKey(offerRequestLeadUuid);

    function showRevealed(detail: PartnerCockpitContactDetail): void {
        queryClient.setQueryData<PartnerCockpitOfferRequestDetail>(queryKey, (lead) =>
            lead && !lead.contact.revealedDetails.includes(detail)
                ? { ...lead, contact: { ...lead.contact, revealedDetails: [...lead.contact.revealedDetails, detail] } }
                : lead,
        );
    }

    const { mutate } = useMutation({
        mutationFn: (detail: PartnerCockpitContactDetail) =>
            revealOfferRequestContactDetail(offerRequestLeadUuid, detail),
        onMutate: async (detail) => {
            await queryClient.cancelQueries({ queryKey });
            const lead = queryClient.getQueryData<PartnerCockpitOfferRequestDetail>(queryKey);
            showRevealed(detail);
            return lead && { offerRequestId: lead.referenceId, customerAccepted: lead.leadStatus === 'engaged' };
        },
        onSuccess: (_result, detail, context) => {
            if (context) {
                track('Offer Request Contact Revealed', {
                    offerRequestId: context.offerRequestId,
                    contactType: contactShortcutOf(detail),
                    customerAccepted: context.customerAccepted,
                });
            }
        },
    });

    return (detail) => (isImpersonating ? showRevealed(detail) : mutate(detail));
}
